import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, BarChart3, Calculator, CheckCircle, AlertCircle } from 'lucide-react';

const ConferLendingAnalysis = ({ onSectionChange }) => {
  const navigate = useNavigate();

  // Colors from the Service Ventures brand
  const colors = {
    navyBlue: '#080b35',
    blue: '#333790',
    lightBlue: '#77a7f7',
    white: '#FFFFFF'
  };

  return (
    <div className="space-y-8">
      {/* Header with Back Button */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/analysis')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Analysis List</span>
        </button>
      </div>

      {/* Analysis Header */}
      <div style={{backgroundColor: colors.blue}} className="text-white p-8 rounded-xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <h1 className="text-3xl font-bold">Confer Lending Performance Analysis</h1>
              <span className="text-xs px-3 py-1 rounded-full font-medium border bg-green-100 text-green-800 border-green-200">
                Q4 2024 - Q1 2025 DATA
              </span>
            </div>
            <div className="flex items-center space-x-6 text-blue-100 text-sm">
              <div className="flex items-center space-x-2">
                <Calculator className="w-4 h-4" />
                <span>Comparative Analysis</span>
              </div>
              <div className="flex items-center space-x-2">
                <span>Updated: November 2025</span>
              </div>
            </div>
          </div>
        </div>
        <p className="text-blue-100 leading-relaxed">
          Comprehensive analysis comparing Cunexus campaigns (Q3 2020 - Q3 2024) versus Confer Lending campaigns 
          (Q4 2024 - Q1 2025), demonstrating significant improvements in loan origination efficiency and member acceptance rates.
        </p>
      </div>

      {/* Key Performance Highlights */}
      <div className="bg-white p-8 rounded-xl border shadow-sm">
        <h3 className="text-2xl font-semibold mb-6 text-gray-900">Executive Summary</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              Confer Lending has demonstrated exceptional performance improvements over our previous vendor Cunexus. 
              The goal of this analysis is to isolate the value Confer's software provides, accounting for external factors such as: 
              Service CU membership size, inflation, and interest rate market.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              Recommendation is to focus on the <span className="font-bold text-blue-600">increase in acceptance rates</span> for calculating Confer's value. 
              The cost savings from switching vendors and the improvement in acceptance rates translate to <span className="font-bold text-blue-600">$398,010 in operational efficiency</span> 
              per campaign.
            </p>
          </div>
          <div className="p-6 rounded-xl " style={{backgroundColor:colors.lightBlue}}>
            <h4 className="font-semibold text-white mb-4">Previous Campaign Value</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-white">Total Efficiency Gain</span>
                <span className="font-bold text-white">$398,010</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Addtl Loan Volume</span>
                <span className="font-bold text-white">$21.2M</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Addtl Loan Value (160bps)</span>
                <span className="font-bold text-white">$336,510</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Vendor Cost Savings</span>
                <span className="font-bold text-white">$61,500</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics Comparison */}
      <div className="bg-white p-8 rounded-xl border shadow-sm">
        <h3 className="text-2xl font-semibold mb-6 flex items-center space-x-2">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          <span>Performance Metrics: Cunexus vs Confer</span>
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

          <div className="text-center p-6 rounded-xl" style={{backgroundColor:colors.blue}}>
            <div className="text-sm text-white font-medium mb-2">Loan $ per Mailing</div>
            <div className="text-4xl font-bold text-white mb-2">+56%</div>
            <div className="text-xs text-white">Increase in dollars of funded loans</div>
          </div>

          <div className="text-center p-6 rounded-xl" style={{backgroundColor:colors.blue}}>
            <div className="text-sm text-white font-medium mb-2">Average Loan Size</div>
            <div className="text-4xl font-bold text-white mb-2">+16%</div>
            <div className="text-xs text-white">Increase in average loan amount</div>
          </div>

          <div className="text-center p-6 rounded-xl" style={{backgroundColor:colors.blue}}>
            <div className="text-sm text-white font-medium mb-2">Acceptance Rate</div>
            <div className="text-4xl font-bold text-white mb-2">+34%</div>
            <div className="text-xs text-white">Increase conversion, better performance</div>
          </div>
        </div>

        {/* Detailed Comparison Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm font-semibold text-gray-900">
                <th className="px-6 py-4">Metric</th>
                <th className="px-4 py-4 text-center">Cunexus</th>
                <th className="px-4 py-4 text-center">Confer</th>
                <th className="px-4 py-4 text-center">Change</th>
                <th className="px-4 py-4 text-center">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">Acceptance Rate</td>
                <td className="px-4 py-4 text-center">2.56%</td>
                <td className="px-4 py-4 text-center">3.44%</td>
                <td className="px-4 py-4 text-center">+34%</td>
                <td className="px-4 py-4 text-center">Best measure for comparing software capabiliy.</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">Loan $ Volume per Mailing</td>
                <td className="px-4 py-4 text-center">$558</td>
                <td className="px-4 py-4 text-center">$871</td>
                <td className="px-4 py-4 text-center">+56%</td>
                <td className="px-4 py-4 text-center">
                  <span className="text-md px-2 py-1">Combination of acceptance rate and average loan amount.</span>
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">Average Loan Amount</td>
                <td className="px-4 py-4 text-center">$21,831</td>
                <td className="px-4 py-4 text-center">$25,343</td>
                <td className="px-4 py-4 text-center">+16%</td>
                <td className="px-4 py-4 text-center">
                  <span className="text-md px-2 py-1">Impacted by inflation and risk tolerance.</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* April 2025 Campaign Deep Dive */}
      <div className="bg-white p-8 rounded-xl border shadow-sm">
        <h3 className="text-2xl font-semibold mb-6 flex items-center space-x-2">
          <Target className="w-6 h-6 text-purple-600" />
          <span>April 2025 Campaign Analysis</span>
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Campaign Metrics */}
          <div className="bg-gray-100 p-6 rounded-xl">
            <h4 className="font-semibold text-gray-900 mb-4">Campaign Metrics</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Total Mailings</span>
                <span className="font-bold text-purple-600">127,694</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Funded Loans</span>
                <span className="font-bold text-blue-600">4,150</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Acceptance Rate</span>
                <span className="font-bold text-green-600">3.250%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Total Funded Amount</span>
                <span className="font-bold text-gray-900">$99,875,845</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Average Loan Size</span>
                <span className="font-bold text-blue-600">$24,066</span>
              </div>
            </div>
          </div>

          {/* Value Calculation */}
          <div className="bg-gray-100 p-6 rounded-xl">
            <h4 className="font-semibold text-gray-900 mb-4">Value Generation - Loan Volume</h4>
            <div className="space-y-4">
              <div className="pb-3 border-b">
                <div className="text-md text-gray-700 mb-1">Cunexus Baseline Volume</div>
                <div className="font-bold text-black">$78,671,435</div>
                <div className="text-md text-gray-700">Using Cunexus' average 2.56% acceptenace rate and determining how much loan volume Cunexus would have driven in this campaign.</div>
              </div>
              <div className="pb-3 border-b border-green-200">
                <div className="text-md text-gray-600 mb-1">Incremental Volume (Confer)</div>
                <div className="font-bold text-black">$21,204,410</div>
                <div className="text-md text-gray-700">Additional loan volume generated by Confer. This is the loan volume we assume Cunexus would not have generated.</div>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <div className="text-md text-gray-700 mb-1">Total Operational Efficiency</div>
                <div className="text-2xl font-bold text-black">$336,510</div>
                <div className="text-md text-gray-700 mt-1">Using an average cost to acquire from indirect auto of 160bps, this is the calculated value of all additional loans generated by Confer.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Value Breakdown */}
        <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
          <h4 className="font-semibold text-gray-900 mb-4">Efficiency Calculation Breakdown</h4>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Contractual Savings</h5>
                  <p className="text-gray-700 text-sm mb-2">
                    Direct price difference between Cunexus and Confer for baseline volume. Cunexus cost $137,500 per campaign & Confer cost $76,000 per campaign.
                  </p>
                  <div className="text-xl font-bold text-blue-600">$61,500</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Incremental Volume Value</h5>
                  <p className="text-gray-700 text-sm mb-2">
                    $21,204,410 additional volume × 160bps acquisition savings.
                  </p>
                  <div className="text-xl font-bold text-blue-600">$336,510</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Total Campaign Value</h5>
                  <p className="text-gray-700 text-sm mb-2">
                    Sum of sontract savings + incremental loan volume value.
                  </p>
                  <div className="text-xl font-bold text-blue-600">$398,010</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      

      {/* Methodology */}
      <div className="bg-white p-8 rounded-xl border shadow-sm">
        <h3 className="text-2xl font-semibold mb-6 flex items-center space-x-2">
          <Calculator className="w-6 h-6 text-blue-600" />
          <span>Analysis Methodology</span>
        </h3>
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Data Period</h4>
                <p className="text-gray-700">
                  Analyzing Cunexus campaigns from Q3 2020 through Q3 2024, compared against Confer Lending 
                  campaigns from Q4 2024 through Q1 2025. Q3 2024 Confer data omitted due to incomplete integration, 
                  and Q3 2025 data excluded due to incomplete reporting period.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Calculation Approach</h4>
                <p className="text-gray-700">
                  Performance calculations account for Confer's increased acceptance rate but do not give credit 
                  for Service CU's strategic decision to increase loan sizes. This conservative approach ensures 
                  we're measuring true software performance improvements rather than policy changes.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Key Assumption</h4>
                <p className="text-gray-700">
                  The 16% increase in average loan amount likely represents Service CU taking on more risk 
                  in our pre-approval lending practices, which is a strategic decision separate from 
                  Confer's performance improvements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConferLendingAnalysis;